/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * The capture surface of a best-list article: the banner ("Already own something?", at the top
 * of the picks), the chooser (shelf → search → the picks) and, once declared, the reference
 * strip. Plan: docs/owner-notes-guide-funnel-plan.md (WP2).
 *
 * The strip asks nothing itself (2026-10-17): the page's ONE owner-review ask sits on the pick
 * the reader owns (`GuidePick`'s `OwnedAsk`), and the strip points at it — or, when what they
 * own is not a pick here, at its product page. The band is still hosted here (one band, one
 * owner) but renders INTO the seat's slot (`useSeatedSlot`, a portal) when there is one, so the
 * form opens under the ask that opened it rather than scrolling the page up to this block.
 *
 * Server-rendered as the BANNER ONLY — the post is `getStaticProps` + edge-cached, so nothing
 * reader-specific is in the HTML (#51). The declaration (localStorage), the chooser, the own
 * note and the band all hydrate client-side. One declared product per page (#63): this block
 * writes it, every pick block reads it.
 *
 * The line is not an ask — it offers a comparison — so it does NOT gate on Turnstile; only the
 * strip's ask does, through `ownerAskVisible` like every other ask (f32a7aeb).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import styles from './GuideCapture.module.css'
import DeclaredStrip, { IconHeadphones } from 'components/owner-notes/DeclaredStrip'
import { OWNER_ASK_ID } from 'components/owner-notes/guide/GuidePick'
import OwnNoteCard from 'components/owner-notes/OwnNoteCard'
import { useNoteBand } from 'components/owner-notes/useNoteBand'
import { SignInGateContext, signInGateFor } from 'lib/owner-notes/signin-gate'
import { bandDraftKey } from 'lib/owner-notes/draft-waiting'
import type { OwnerBandLocation } from 'components/owner-notes/OwnerNoteBand'
import { useWriterHandoff } from 'lib/community/use-writer-handoff'
import { publicConfig } from 'config'
import { brandedTitle } from 'lib/dealhunt/url'
import type {
  DealHuntSuggestHit,
  OwnerClaimResponse,
  OwnerPublishResponse,
  OwnerShelfItem,
} from 'lib/dealhunt/types'
import { useOnScreenOnce, useOwnerTrack } from 'lib/owner-notes/analytics'
import {
  NO_OWN_NOTE,
  onOpenOwnerBand,
  publishOwnNote,
  sectionOwnNoteSignal,
  type OwnNoteSignal,
  type OwnerBandOrigin,
  useSeatedSlot,
  useSeatedSurface,
} from 'lib/owner-notes/bus'
import { hasDeviceMarker, useDeclaredProduct } from 'lib/owner-notes/declared'
import {
  prefetchDeclaration,
  seedFromHit,
  useDeclaredOwners,
  usePendingDeclare,
  type DeclareSeed,
} from 'lib/owner-notes/declaration'
import { ownViewsFromMe, refreshOwnNotes, useOwnNote } from 'lib/owner-notes/own-note'
import { sharedMe } from 'lib/owner-notes/api'
import { useOwnTypeahead } from 'lib/owner-notes/own-typeahead'
import { turnstileConfigured } from 'lib/owner-notes/turnstile'
import {
  GATED_GUIDE_COPY,
  GUIDE_COPY,
  ownerAskVisible,
  SIGN_IN_NEEDED,
  ownersDeepLink,
  ownersFromCard,
  ownStatusLabel,
  switchLine,
  SWITCH_LABEL,
  UNDECLARE_LABEL,
} from 'lib/owner-notes/band-copy'
import type { DSGuidePickRef, DSNCOwnerGuideCapture } from 'types.sg.ds'

interface GuideCaptureProps {
  data: DSNCOwnerGuideCapture
}

/**
 * What the guide's band reports as `location` on `band_opened`: the ask that opened it —
 * `strip` / `roster` / `seated` / `rail`, `own_note` for an edit — so each ask's opens sit under
 * its own `ask_shown` (2026-10-07; every guide open reported `guide` before). `guide` stays the
 * value for a request that names no ask. The banner (`guide_line`) and the chart line (`chart`)
 * open the chooser, never the band, so neither is ever a `band_opened` location.
 */
export function guideBandLocation(origin?: OwnerBandOrigin): OwnerBandLocation {
  return origin ?? 'guide'
}

const GuideCapture: React.VFC<GuideCaptureProps> = ({ data }) => {
  const { declared, declare } = useDeclaredProduct()
  const product = useMemo(() => {
    if (!declared?.nodeId) {
      return null
    }
    return { nodeId: declared.nodeId, name: declared.name, href: declared.href }
  }, [declared?.nodeId, declared?.name, declared?.href])
  const trackOwner = useOwnerTrack(product)
  const trailingSlash = publicConfig('useTrailingSlash')

  // The chooser opens in place of the banner, and nowhere else (2026-10-17: the pick blocks'
  // own openers, and the overlay seat they had, are gone — the banner is the one declare ask).
  const [chooserOpen, setChooserOpen] = useState(false)
  const [resolveError, setResolveError] = useState<string | null>(null)
  const [shelf, setShelf] = useState<OwnerShelfItem[]>([])
  // "Own another? Switch" pressed while a note existed: the chooser says the note stays up.
  const [switchedFrom, setSwitchedFrom] = useState<string | null>(null)
  const {
    me,
    ownNote,
    claimed,
    pending: ownPending,
    applyResult,
    applyClaim,
    clearDevice,
  } = useOwnNote(declared?.nodeId ?? null)
  // One Review Writer (P1): with the flag on for `guide`, the strip's Yes / No (and the rail and
  // row asks, `GuideRailAsk` / `GuidePick`) link the community writer; the form is not warmed.
  const handoff = useWriterHandoff('strip', declared?.href, { flag: 'guide' })
  // The sign-in gate (slice D): the banner, the strip, the roster / seated / rail asks
  // (`GuidePick`, `GuideRailAsk`) say it, and the page's one band — hosted here — is gated.
  const gated = signInGateFor('guide')
  // The form under the strip (`useNoteBand`): warmed on idle once an ask can be made.
  const band = useNoteBand(product && turnstileConfigured() && !handoff ? product.nodeId : null)
  const { bandOpen, open: openBand, close: closeBand } = band
  // The ask that opened the band (`guideBandLocation`), read by its `band_opened`.
  const [bandLocation, setBandLocation] = useState<OwnerBandLocation>('guide')

  const searchRef = useRef<HTMLInputElement>(null)
  const blockRef = useRef<HTMLElement>(null)
  const bandSlotRef = useRef<HTMLDivElement>(null)
  // A far ask opened the band: bring the band (not the whole block) to the reader once it is in.
  const scrollToBand = useRef(false)
  // One impression for the line; the strip asks nothing (2026-10-17), so it reports none.
  const lineShown = useRef(false)

  // One render after mount: the point at which `declared` is the reader's real answer rather
  // than "the provider has not read localStorage yet".
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  // A pick's block is holding the reader's own note or the ask: the strip shows its status chip
  // and leaves the card to it (one note, one card — Directive 3b903d15), and the band renders
  // into that block's slot.
  const seatedShown = useSeatedSurface()
  const seatedSlot = useSeatedSlot()

  // The line's impression, once, and only when the line is actually on screen (≥ 30 % of the
  // block in the viewport — since 2026-09-18; before, "rendered"). `declared` is null on the
  // first render for EVERY reader — the provider reads localStorage in an effect, which runs
  // after this component's — so firing on mount counted returning declared readers as line
  // impressions and flattered the declared-per-ask rate. `mounted` costs one render and makes
  // "null" mean undeclared. The strip's own impression is separate.
  const lineAsking = mounted && !declared
  const lineSeen = useOnScreenOnce(blockRef, lineAsking)
  useEffect(() => {
    if (lineAsking && lineSeen && !lineShown.current) {
      lineShown.current = true
      trackOwner('ask_shown', { location: 'guide_line' })
    }
  }, [lineAsking, lineSeen, trackOwner])

  // What every other owner surface on the article reads off the bus:
  //  - the rail (`railAskMode`): nothing while `/me` is pending, nothing for an author;
  //  - the section pick's SEATED ask (`GuidePick`, WP3): it would re-open the band this block
  //    already has open, so `bandOpen` tells it to stand down — the band is the strip's.
  // `pending` keeps the section's rule (`sectionOwnNoteSignal`): a `/me` refresh under an open
  // band — the device minted at Publish — is not an unknown reader.
  useEffect(() => {
    const signal: OwnNoteSignal & { bandOpen: boolean } = {
      ...sectionOwnNoteSignal({ pending: ownPending, hasNote: ownNote !== null, bandOpen }),
      bandOpen,
    }
    publishOwnNote(signal)
  }, [ownPending, ownNote, bandOpen])
  useEffect(() => () => publishOwnNote(NO_OWN_NOTE), [])

  // The chooser's quick picks: this browser's shelf. `useOwnNote` only reads `/me` once a
  // product is declared, so an undeclared reader's shelf is asked for here — once, and only
  // with a device marker (no device = no profile to read). The same `/me` the declaration's
  // `useOwnNote` then reuses (`sharedMe`).
  const shelfAsked = useRef(false)
  useEffect(() => {
    if (!chooserOpen || shelfAsked.current || !hasDeviceMarker()) {
      return
    }
    shelfAsked.current = true
    sharedMe().then(
      res => setShelf(res.shelf),
      () => undefined
    )
  }, [chooserOpen])
  const shelfItems = shelf.length > 0 ? shelf : me?.shelf ?? []

  useEffect(() => {
    if (chooserOpen) {
      searchRef.current?.focus()
    }
  }, [chooserOpen])

  // The declared pick, when the reader owns one of this guide's picks: its context already
  // carries the owner counts DealHunt resolved at build time. Alias-aware (#110).
  const declaredNodeId = declared?.nodeId ?? null
  const declaredPick = useMemo(
    () =>
      data.picks.find(
        p =>
          p.context !== null &&
          declaredNodeId !== null &&
          (p.context.node_id === declaredNodeId || p.context.node_ids.includes(declaredNodeId))
      ) ?? null,
    [data.picks, declaredNodeId]
  )

  // Owner counts for the strip's question: the pick's build-time context, else what the
  // declaration's resolve said, else one cheap public read (`useDeclaredOwners`).
  const declaredOwners = useDeclaredOwners(declaredNodeId, Boolean(declaredPick))
  const ownersSummary = declaredPick?.context
    ? ownersFromCard(declaredPick.context.owners)
    : declaredOwners.owners

  // The rail ask (`GuideRailAsk`, in the sidebar), the reader's own roster row and the seated
  // ask ask us to open the band down here — naming themselves, and the roster row with the
  // buy-again answer it was given. The seated card's Edit (`own_note`) opens it on the note.
  useEffect(
    () =>
      onOpenOwnerBand((origin, buyAgain) => {
        setBandLocation(guideBandLocation(origin))
        // The seat's own asks (the reader's row or in-depth block, the card's Edit) open the
        // band where the reader already is; the rail is a screen away from it, so that one
        // still scrolls (the band is in the seat's slot, or here when there is no seat).
        scrollToBand.current = origin === 'rail' || origin === undefined
        openBand(origin === 'own_note', buyAgain)
      }),
    [openBand]
  )
  // Once the band is in: its top to the top of the screen, below the sticky product bar
  // (`.bandSlot`'s scroll-margin-top) — centring the whole block left the band's head, and on
  // a phone the textarea, under the bar.
  useEffect(() => {
    if (bandOpen && scrollToBand.current) {
      scrollToBand.current = false
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      bandSlotRef.current?.scrollIntoView({ block: 'start', behavior: reduced ? 'auto' : 'smooth' })
    }
  }, [bandOpen])

  // The strip shows at once from what the seed knows; the chooser stays open underneath, so a
  // failed lookup lands the reader back in it with the error (`usePendingDeclare`).
  const pending = usePendingDeclare(declare, trailingSlash)
  const declareFrom = async (seed: DeclareSeed, location: string): Promise<void> => {
    setResolveError(null)
    setSwitchedFrom(null)
    try {
      const resolved = await pending.start(seed)
      if (!resolved) {
        return
      }
      declaredOwners.setResolved(resolved.owners)
      declare(resolved.declared)
      setChooserOpen(false)
      trackOwner('declared', {
        location,
        target_product_id: resolved.declared.nodeId,
        target_product_name: seed.name,
      })
      if (!resolved.declared.nodeId) {
        setResolveError(GUIDE_COPY.errors.noPage)
      }
    } catch {
      setResolveError(GUIDE_COPY.errors.lookup)
    }
  }

  const declareHit = (hit: DealHuntSuggestHit): Promise<void> =>
    declareFrom(seedFromHit(hit), 'search')

  const declareShelfItem = (item: OwnerShelfItem): Promise<void> =>
    declareFrom(
      {
        slug: item.node_slug,
        brand: item.brand_name ?? null,
        brandSlug: item.brand_slug ?? null,
        name: item.node_name,
        imageUrl: item.image_url,
        priceCents: null,
        sgScore: null,
        nodeId: item.node_id,
      },
      'shelf'
    )

  /** A pick the reader owns: its context IS the resolve, so no lookup is made at all. */
  const declarePick = (pick: DSGuidePickRef): void => {
    const { context } = pick
    if (!context) {
      return
    }
    setResolveError(null)
    setSwitchedFrom(null)
    declare({
      nodeId: context.node_id,
      slug: context.full_slug,
      href: context.sg_href,
      name: context.name,
      imageUrl: context.image_url,
      axes: context.compare.axes,
      batteryHours: context.compare.batteryHours,
      // Never part of a comparison (Constraint f80d2e70), so never stored as one.
      priceCents: null,
      sgScore: null,
      clearbuyProductId: context.compare.clearbuyProductId,
      compareSpecs: context.compare.compareSpecs,
      declaredAt: Date.now(),
    })
    setChooserOpen(false)
    trackOwner('declared', {
      location: 'pick',
      target_product_id: context.node_id,
      target_product_name: context.name,
    })
  }

  const typeahead = useOwnTypeahead(
    hit => void declareHit(hit),
    hit => prefetchDeclaration(seedFromHit(hit))
  )

  const onResult = (result: OwnerPublishResponse): void => {
    applyResult(result)
    // The seat's block reads its own `/me` (`useOwnNote`), not this one's patch: re-read it,
    // so the new note lands in the seat rather than up here (2026-10-17).
    refreshOwnNotes()
  }
  // #120: the claim patches this page's own card only when DealHunt names its note.
  const onClaimedHere = (r: OwnerClaimResponse): void => {
    applyClaim(r)
  }

  const configured = turnstileConfigured()
  // Whether an ask could be made at all (the hydrated rule: a browser with a device waits for
  // its own note, f32a7aeb) — the strip itself asks nothing; this decides whether it points at
  // the seat or stays quiet.
  const askVisible = ownerAskVisible({
    configured,
    hydrated: true,
    hasOwnNote: ownNote !== null,
    bandOpen,
    ownPending,
  })

  const clearLabel = ownNote ? SWITCH_LABEL : UNDECLARE_LABEL
  /**
   * The note is the durable thing, the declaration a local baseline: switching never touches it
   * (docs/owner-notes-after-publish.md). The chooser reopens in place, saying so.
   */
  const clear = (reopen: boolean): void => {
    pending.cancel()
    setSwitchedFrom(ownNote && declared ? declared.name : null)
    declare(null)
    closeBand()
    setChooserOpen(reopen)
  }

  const chooser = (
    <div className={styles.chooser}>
      <p className={styles.chooserTitle}>{GUIDE_COPY.chooser.title}</p>
      <p className={styles.chooserFine}>{GUIDE_COPY.chooser.any}</p>
      {gated && <p className={styles.chooserFine}>{SIGN_IN_NEEDED}</p>}
      {switchedFrom && <p className={styles.switchLine}>{switchLine(switchedFrom)}</p>}
      {shelfItems.length > 0 && (
        <div className={styles.chips}>
          <span className={styles.chipsLabel}>{GUIDE_COPY.chooser.shelf}</span>
          {shelfItems.map(item => (
            <button
              key={item.node_id}
              type="button"
              className={styles.chip}
              onClick={() => void declareShelfItem(item)}
            >
              {item.node_name}
            </button>
          ))}
        </div>
      )}
      <div ref={typeahead.rootRef} className={styles.search}>
        <input
          ref={searchRef}
          type="text"
          className={styles.input}
          role="combobox"
          aria-expanded={typeahead.showDropdown}
          aria-controls="guide-capture-results"
          aria-label={GUIDE_COPY.chooser.searchLabel}
          placeholder={GUIDE_COPY.chooser.searchPlaceholder}
          autoComplete="off"
          value={typeahead.query}
          onFocus={() => typeahead.setOpen(true)}
          onChange={e => {
            typeahead.setQuery(e.target.value)
            typeahead.setOpen(true)
          }}
          onKeyDown={typeahead.onKeyDown}
        />
        {typeahead.showDropdown && (
          <div className={styles.dropdown} id="guide-capture-results" role="listbox">
            {typeahead.results.map((hit, i) => (
              <button
                key={hit.objectID}
                type="button"
                role="option"
                aria-selected={i === typeahead.highlight}
                className={clsx(styles.hit, { [styles.hitOn]: i === typeahead.highlight })}
                onMouseEnter={() => typeahead.setHighlight(i)}
                onClick={() => typeahead.select(hit)}
              >
                {brandedTitle(hit.brand, hit.canonicalName || hit.title)}
              </button>
            ))}
            {!typeahead.results.length && (
              <p className={styles.notice}>
                {typeahead.loading
                  ? GUIDE_COPY.chooser.searching
                  : GUIDE_COPY.chooser.nothing(typeahead.trimmed)}
              </p>
            )}
          </div>
        )}
      </div>
      {data.picks.length > 0 && (
        <div className={styles.chips}>
          <span className={styles.chipsLabel}>{GUIDE_COPY.chooser.picks}</span>
          {data.picks.map(pick => (
            <button
              key={pick.clearBuyProductId}
              type="button"
              className={styles.chip}
              disabled={pick.context === null}
              title={pick.context === null ? GUIDE_COPY.chooser.pickDisabled : undefined}
              onClick={() => declarePick(pick)}
            >
              {pick.context?.name ?? pick.title}
            </button>
          ))}
        </div>
      )}
      <button type="button" className={styles.chooserClose} onClick={() => setChooserOpen(false)}>
        {GUIDE_COPY.chooser.close}
      </button>
    </div>
  )

  if (!declared) {
    return (
      <section className={styles.block} aria-label={GUIDE_COPY.line.title} ref={blockRef}>
        {chooserOpen ? (
          chooser
        ) : (
          <div className={styles.line}>
            <span className={styles.mark}>
              <IconHeadphones />
            </span>
            <div className={styles.lineMain}>
              <p className={styles.lineTitle}>{GUIDE_COPY.line.title}</p>
              <p className={styles.lineBody}>
                {gated ? GATED_GUIDE_COPY.line.body : GUIDE_COPY.line.body}
              </p>
            </div>
            <div className={styles.action}>
              <button type="button" className={styles.lineCta} onClick={() => setChooserOpen(true)}>
                {GUIDE_COPY.line.cta}
              </button>
              <p className={styles.actionNote}>
                {gated ? GATED_GUIDE_COPY.line.note : GUIDE_COPY.line.note}
              </p>
            </div>
          </div>
        )}
        {resolveError && <p className={styles.error}>{resolveError}</p>}
      </section>
    )
  }

  // Where the strip points instead of asking (2026-10-17): the seat, when a pick's block has
  // taken it (`seatedShown` — the block exists and is asking, whatever `data.picks` says: a
  // pick with no section has no seat, a pick with no context can still be one); else the
  // product page's owner reviews. Nothing once they have a note (the status chip says so) or
  // while the band is open.
  let aside: React.ReactNode = null
  if (product && askVisible && seatedShown) {
    aside = (
      <a href={`#${OWNER_ASK_ID}`} className={styles.seatLink}>
        {GUIDE_COPY.strip.seatLink(ownersSummary?.count ?? 0)}
      </a>
    )
  } else if (product && askVisible && declared.href) {
    aside = (
      <a href={ownersDeepLink(declared.href)} className={styles.seatLink}>
        {GUIDE_COPY.strip.elsewhere}
      </a>
    )
  }

  // The page's one band. In the seat's slot when a pick's block offers one (the reader owns a
  // pick on this page), else under the strip.
  const bandNode = product && bandOpen && band.OwnerNoteBand && (
    <div className={styles.bandSlot} ref={bandSlotRef}>
      <SignInGateContext.Provider value={gated}>
        <band.OwnerNoteBand
          product={product}
          existing={band.editing ? ownNote : null}
          seedBuyAgain={band.seedBuyAgain}
          location={bandLocation}
          trackOwner={trackOwner}
          onClose={closeBand}
          onResult={onResult}
          me={me}
          onDeviceForgotten={clearDevice}
          onClaimed={onClaimedHere}
          draftKey={bandDraftKey(product.nodeId)}
        />
      </SignInGateContext.Provider>
    </div>
  )

  return (
    <section
      className={styles.block}
      aria-label={GUIDE_COPY.strip.lead(declared.name)}
      ref={blockRef}
    >
      <DeclaredStrip
        declared={declared}
        lead={GUIDE_COPY.strip.lead(declared.name)}
        body={GUIDE_COPY.strip.body}
        statusLabel={ownNote ? ownStatusLabel(ownNote.status) : null}
        clearLabel={clearLabel}
        onClear={() => clear(true)}
        onRemove={() => clear(false)}
        ask={null}
        aside={aside}
      />
      {/* The author's note, where the ask was: Edit, "See it on the product page" and — on a
          live anonymous note — the keep-it-yours sign-in, which `OwnNoteCard` renders with its
          own `useKeepSurface` so the app-wide prompt stays away (Directive 3b903d15). No
          Delete: the full control surface stays on the product page, as on the category tile.
          Not here at all when the pick the reader owns has a block of its own: that block
          seats the note, the strip keeps the status chip on its lead line. */}
      {product && ownNote && !bandOpen && !seatedShown && (
        <div className={styles.ownNote}>
          <OwnNoteCard
            note={ownNote}
            claimed={claimed}
            trackOwner={trackOwner}
            productHref={declared.href}
            location="own_note"
            // The author's readers row, as on the product page (Guillaume, 2026-09-22).
            views={ownViewsFromMe(me, declared.nodeId)}
            onEdit={() => {
              setBandLocation('own_note')
              openBand(true)
            }}
            onClaimed={onClaimedHere}
          />
        </div>
      )}
      {bandNode && (seatedSlot ? createPortal(bandNode, seatedSlot) : bandNode)}
      {resolveError && <p className={styles.error}>{resolveError}</p>}
    </section>
  )
}

export default GuideCapture
